import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import Helmet from 'react-helmet';
import CurrentPageClientProvider from '../CurrentPageClientProvider';
import * as classes from '../../../../modules/theme/classes';

const propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  currentSite: PropTypes.shape({
    primaryColor: PropTypes.string.isRequired,
    secondaryColor: PropTypes.string.isRequired,
    tertiaryColor: PropTypes.string.isRequired,
    publicLogo: PropTypes.shape({
      alt: PropTypes.string.isRequired,
      link: PropTypes.shape({
        href: PropTypes.string.isRequired,
        target: PropTypes.string.isRequired,
      }),
      title: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }).isRequired,
    theme: PropTypes.shape({
      elements: PropTypes.shape({
        h1: PropTypes.shape({
          color: PropTypes.string,
        }).isRequired,
        h2: PropTypes.shape({
          color: PropTypes.string,
        }).isRequired,
        h3: PropTypes.shape({
          color: PropTypes.string,
        }).isRequired,
        h4: PropTypes.shape({
          color: PropTypes.string,
        }).isRequired,
      }).isRequired,
      fonts: PropTypes.shape({
        primary: PropTypes.shape({
          name: PropTypes.string,
        }).isRequired,
        secondary: PropTypes.shape({
          name: PropTypes.string,
        }).isRequired,
      }),
      buttons: PropTypes.shape({
        backgroundColor: PropTypes.string,
        hoverBackgroundColor: PropTypes.string,
        activeBackgroundColor: PropTypes.string,
        borderRadius: PropTypes.string,
        textAlign: PropTypes.string,
        textTransform: PropTypes.string,
      }),
      map: PropTypes.shape({
        pins: PropTypes.shape({
          primary: PropTypes.shape({
            data: PropTypes.string,
          }),
          secondary: PropTypes.shape({
            color: PropTypes.string,
            data: PropTypes.string,
            label: PropTypes.shape({
              x: PropTypes.number.isRequired,
              y: PropTypes.number.isRequired,
            }),
            type: PropTypes.string.isRequired,
          }).isRequired,
        }).isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  facility: PropTypes.shape({
    primaryColor: PropTypes.string,
    secondaryColor: PropTypes.string,
    tertiaryColor: PropTypes.string,
    publicLogo: PropTypes.shape({
      alt: PropTypes.string,
      link: PropTypes.shape({
        href: PropTypes.string,
        target: PropTypes.string,
      }),
      title: PropTypes.string,
      url: PropTypes.string,
    }),
  }),
};

const defaultProps = {
  facility: undefined,
};

const CurrentPageServerProvider = ({
  children,
  currentSite,
  facility,
}) => {
  const {
    primaryColor: currentSitePrimaryColor,
    secondaryColor: currentSiteSecondaryColor,
    tertiaryColor: currentSiteTertiaryColor,
    theme: {
      elements: {
        h1: {
          color: h1ElementColor,
        },
        h2: {
          color: h2ElementColor,
        },
        h3: {
          color: h3ElementColor,
        },
        h4: {
          color: h4ElementColor,
        },
      },
      fonts: {
        primary: {
          name: primaryFontFamily,
        },
        secondary: {
          name: secondaryFontFamily,
        },
      },
    },
  } = currentSite;

  const {
    primaryColor: facilityPrimaryColor,
    secondaryColor: facilitySecondaryColor,
    tertiaryColor: facilityTertiaryColor,
  } = facility || {};

  const primaryColor = facilityPrimaryColor || currentSitePrimaryColor;
  const secondaryColor = facilitySecondaryColor || currentSiteSecondaryColor;
  const tertiaryColor = facilityTertiaryColor || currentSiteTertiaryColor;

  const h1Color = h1ElementColor || primaryColor;
  const h2Color = h2ElementColor || primaryColor;
  const h3Color = h3ElementColor || secondaryColor;
  const h4Color = h4ElementColor || secondaryColor;

  const primaryFontFamilyClass = primaryFontFamily && classes.PRIMARY_FONT_FAMILY_CLASS;
  const secondaryFontFamilyClass = primaryFontFamily && classes.SECONDARY_FONT_FAMILY_CLASS;

  const buttonBorderRadius = currentSite?.theme?.buttons?.borderRadius || '0px';
  const buttonTextTransform = currentSite?.theme?.buttons?.textTransform || 'uppercase';

  /* eslint-disable react/jsx-closing-tag-location */
  return (
    <React.Fragment>
      <Helmet>
        <style>{`
          .${classes.PRIMARY_COLOR_BACKGROUND_CLASS} {
            background-color: ${primaryColor} !important;
          }
          .${classes.BUTTON_STYLE_CLASS} {
            border-radius: ${buttonBorderRadius} !important;
            text-transform: ${buttonTextTransform} !important;
          }
          .${classes.PRIMARY_COLOR_BACKGROUND_HOVER_CLASS}:hover {
            background-color: ${primaryColor} !important;
          }
          .${classes.PRIMARY_COLOR_BORDER_CLASS} {
            border-color: ${primaryColor} !important;
          }
          .${classes.PRIMARY_COLOR_BORDER_HOVER_CLASS}:hover {
            border-color: ${primaryColor} !important;
          }
          .${classes.PRIMARY_COLOR_FILL_CLASS} {
            fill: ${primaryColor} !important;
          }
          .${classes.PRIMARY_COLOR_CLASS} {
            color: ${primaryColor} !important;
          }
          .${classes.PRIMARY_COLOR_HOVER_CLASS}:hover {
            color: ${primaryColor} !important;
          }
          .${classes.SECONDARY_COLOR_BACKGROUND_CLASS} {
            background-color: ${secondaryColor} !important;
          }
          .${classes.SECONDARY_COLOR_BACKGROUND_HOVER_CLASS}:hover {
            background-color: ${secondaryColor} !important;
          }
          .${classes.SECONDARY_COLOR_BORDER_CLASS} {
            border-color: ${secondaryColor} !important;
          }
          .${classes.SECONDARY_COLOR_BORDER_HOVER_CLASS}:hover {
            border-color: ${secondaryColor} !important;
          }
          .${classes.SECONDARY_COLOR_FILL_CLASS} {
            fill: ${secondaryColor} !important;
          }
          .${classes.SECONDARY_COLOR_CLASS} {
            color: ${secondaryColor} !important;
          }
          .${classes.SECONDARY_COLOR_HOVER_CLASS}:hover {
            color: ${secondaryColor} !important;
          }
          .${classes.TERTIARY_COLOR_BACKGROUND_CLASS} {
            background-color: ${tertiaryColor} !important;
          }
          .${classes.TERTIARY_COLOR_BACKGROUND_HOVER_CLASS}:hover {
            background-color: ${tertiaryColor} !important;
          }
          .${classes.TERTIARY_COLOR_BORDER_CLASS} {
            border-color: ${tertiaryColor} !important;
          }
          .${classes.TERTIARY_COLOR_BORDER_HOVER_CLASS}:hover {
            border-color: ${tertiaryColor} !important;
          }
          .${classes.TERTIARY_COLOR_FILL_CLASS} {
            fill: ${tertiaryColor} !important;
          }
          .${classes.TERTIARY_COLOR_CLASS} {
            color: ${tertiaryColor} !important;
          }
          .${classes.TERTIARY_COLOR_HOVER_CLASS}:hover {
            color: ${tertiaryColor} !important;
          }
          .theme h1 {
            color: ${h1Color} !important;
          }
          .theme h2 {
            color: ${h2Color} !important;
          }
          .theme h3 {
            color: ${h3Color} !important;
          }
          .theme h4 {
            color: ${h4Color} !important;
          }

        `}</style>
        {primaryFontFamily && (
          <style>{`
            .${primaryFontFamilyClass} {
              font-family: ${primaryFontFamily} !important;
            }

          `}</style>
        )}
        {secondaryFontFamily && (
          <style>{`
            .${secondaryFontFamilyClass} {
              font-family: ${secondaryFontFamily} !important;
            }
          `}</style>
        )}
      </Helmet>
      <CurrentPageClientProvider currentSite={currentSite} facility={facility}>
        {children}
      </CurrentPageClientProvider>
    </React.Fragment>
  );
  /* eslint-enable react/jsx-closing-tag-location */
};

CurrentPageServerProvider.propTypes = propTypes;
CurrentPageServerProvider.defaultProps = defaultProps;

// TODO: This is not being used currently and instead data for this component
// is being pulled from the Redux store.
CurrentPageServerProvider.fragments = {
  currentSite: gql`
    fragment CurrentServerPageProviderCurrentSite on Site {
      primaryColor
      secondaryColor
      tertiaryColor
      publicLogo {
        alt
        link {
          href
          target
        }
        title
        url
      }
      theme {
        fonts {
          primary {
            name
          }
          secondary {
            name
          }
        }
        buttons {
          backgroundColor
          hoverBackgroundColor
          activeBackgroundColor
          borderRadius
          textAlign
        }
        map {
          pins {
            primary {
              data
            }
            secondary {
              color
              label {
                x
                y
              }
              data
              type
            }
          }
        }
      }
    }
  `,
  facility: gql`
    fragment CurrentPageProviderFacility on Facility {
      primaryColor
      secondaryColor
      tertiaryColor
      publicLogo {
        alt
        link {
          href
          target
        }
        title
        url
      }
    }
  `,
};

export default CurrentPageServerProvider;
